// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { KeyRound, RefreshCw } from 'lucide-react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { IdpCheckList } from '@/components/greenlight/idp/IdpCheckList';
import { IdpProviderFields, IdpProviderPicker } from '@/components/greenlight/idp/IdpProviderForm';
import {
  joinHostedDomains,
  useIdpForm,
  type IdpFormInitial,
} from '@/components/greenlight/idp/useIdpForm';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { OrgSettings, RoleSource } from '@greenlight/shared';
import type { IdpValidationResult } from '@greenlight/contract';

import { SettingsSectionHeading } from './SettingsSectionHeading';
import { ToggleRow } from './ToggleRow';

type Provider = 'entra' | 'okta' | 'google' | 'oidc';

const PROVIDER_DISPLAY_NAMES: Record<Provider, string> = {
  entra: 'Microsoft Entra ID',
  okta: 'Okta',
  google: 'Google Workspace',
  oidc: 'OpenID Connect',
};

const PROVIDER_MONOGRAMS: Record<Exclude<Provider, 'google'>, string> = {
  entra: 'M',
  okta: 'O',
  oidc: 'ID',
};

/** The configuration the test-connection action verifies before a save. */
export interface IdentityCandidate {
  issuer_url: string;
  provider: Provider;
  client_id: string;
  /** Omitted on a same-provider edit; the server verifies the stored secret. */
  client_secret?: string;
}

/** The test-connection outcome, with the per-check rows both surfaces render. */
export interface IdentityVerification {
  ok: boolean;
  message: string;
  checks?: IdpValidationResult['checks'];
}

/** The body the reconfigure form submits — matches the contract's ReconfigureIdentityBody. */
export interface ReconfigureIdentityInput {
  provider: Provider;
  issuer_url: string;
  client_id: string;
  client_secret?: string;
  /** Generic OIDC only: the sign-in button label. */
  display_name?: string;
  /** Where roles come from (docs/10). Google is server-constrained to greenlight. */
  role_source?: RoleSource;
  group_claim: string;
  role_groups?: { org_admin?: string[]; admin?: string[]; user?: string[] };
  /** Google only; empty means no Greenlight-side domain restriction. */
  hosted_domains?: string[];
}

/**
 * 40×40 provider identity tile. Google reuses the local brand SVG; Entra and
 * Okta use a neutral monogram — no network image deps, correct on both themes.
 */
function ProviderTile({ provider }: { provider: Provider }): React.JSX.Element {
  const baseClasses =
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-gl-icon border border-border';
  if (provider === 'google') {
    return (
      <span
        aria-hidden="true"
        className={cn(baseClasses, 'bg-card')}
        style={{ width: 40, height: 40 }}
      >
        <img src="/brands/google.svg" alt="" width={24} height={24} aria-hidden="true" />
      </span>
    );
  }
  const letter = PROVIDER_MONOGRAMS[provider];
  return (
    <span
      aria-hidden="true"
      className={cn(baseClasses, 'bg-muted font-mono font-semibold text-foreground')}
      style={{ width: 40, height: 40, fontSize: 15 }}
    >
      {letter}
    </span>
  );
}

export interface IdentitySettingsProps {
  identity: OrgSettings['identity'];
  /** The org's current role-management mode (docs/10). */
  roleSource: OrgSettings['role_source'];
  /** Test the currently-stored connection (stamps last_tested_at). */
  onTestConnection: () => Promise<IdentityVerification | null>;
  /** Verify a candidate configuration before saving (docs/12 §Validate before save). */
  onVerifyIdp: (candidate: IdentityCandidate) => Promise<IdentityVerification>;
  /** Persist the reconfigure. Returns ok, or an error message to surface inline. */
  onReconfigure: (body: ReconfigureIdentityInput) => Promise<{ ok: boolean; error?: string }>;
  /** Turn login-time IdP group display-name resolution on or off (Entra only). */
  onToggleGroupNameResolution: (resolve_group_names: boolean) => Promise<void>;
  className?: string;
}

export function IdentitySettings({
  identity,
  roleSource,
  onTestConnection,
  onVerifyIdp,
  onReconfigure,
  onToggleGroupNameResolution,
  className,
}: IdentitySettingsProps): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);

  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Identity" pill="Org admin" />
      {editing || identity === undefined ? (
        <ReconfigureForm
          identity={identity}
          roleSource={roleSource}
          onVerifyIdp={onVerifyIdp}
          onReconfigure={onReconfigure}
          onDone={() => setEditing(false)}
          allowCancel={identity !== undefined}
        />
      ) : (
        <IdentitySummary
          identity={identity}
          roleSource={roleSource}
          onTestConnection={onTestConnection}
          onReconfigure={() => setEditing(true)}
          onToggleGroupNameResolution={onToggleGroupNameResolution}
        />
      )}
    </div>
  );
}

// ── Read-only summary ─────────────────────────────────────────────────────────

function IdentitySummary({
  identity,
  roleSource,
  onTestConnection,
  onReconfigure,
  onToggleGroupNameResolution,
}: {
  identity: NonNullable<OrgSettings['identity']>;
  roleSource: OrgSettings['role_source'];
  onTestConnection: () => Promise<IdentityVerification | null>;
  onReconfigure: () => void;
  onToggleGroupNameResolution: (resolve_group_names: boolean) => Promise<void>;
}): React.JSX.Element {
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<IdentityVerification | null>(null);
  const [savingGroupNames, setSavingGroupNames] = React.useState(false);
  async function handleTest(): Promise<void> {
    if (testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await onTestConnection());
    } finally {
      setTesting(false);
    }
  }
  const adminGroup = identity.role_groups?.org_admin?.[0];
  const userGroup = identity.role_groups?.user?.[0];
  return (
    <div className="rounded-gl-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 h-[56px] bg-muted/40 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <ProviderTile provider={identity.provider} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] font-bold text-foreground leading-tight">
              {PROVIDER_DISPLAY_NAMES[identity.provider]}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground leading-tight">
              OpenID Connect ·{' '}
              {identity.last_tested_at !== null
                ? `tested ${relativeTime(identity.last_tested_at)}`
                : 'never tested'}
            </span>
          </div>
        </div>
        <StatusBadge variant="active">Connected</StatusBadge>
      </div>

      <div className="divide-y divide-border">
        <SummaryRow label="OIDC issuer" mono value={identity.oidc_issuer} />
        <SummaryRow label="Client ID" mono value={identity.client_id} />
        {identity.provider === 'oidc' ? (
          <SummaryRow label="Sign-in button" value={identity.display_name ?? 'SSO'} />
        ) : null}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5">
          <span className="text-sm text-foreground">Client secret</span>
          <span className="font-mono text-[12px] text-muted-foreground">
            Stored in Key Vault · never shown
          </span>
        </div>
        <SummaryRow
          label="Role management"
          value={roleSource === 'greenlight' ? 'Greenlight-managed' : 'IdP-managed'}
        />
        {identity.provider !== 'google' && roleSource === 'idp' && (
          <SummaryRow label="Group claim" mono value={identity.group_claim} />
        )}
        {roleSource === 'idp' && adminGroup ? (
          <SummaryRow label="Org admin group" mono value={adminGroup} />
        ) : null}
        {roleSource === 'idp' && userGroup ? (
          <SummaryRow label="User group" mono value={userGroup} />
        ) : null}
        {identity.provider === 'google' ? (
          <SummaryRow
            label="Workspace domains"
            mono={Boolean(identity.hosted_domains?.length)}
            value={
              identity.hosted_domains?.length
                ? identity.hosted_domains.join(', ')
                : 'Any domain your OAuth client admits'
            }
          />
        ) : null}
        {identity.provider === 'entra' ? (
          <ToggleRow
            label="Show group names instead of IDs"
            helper="Grant tenant admin consent for GroupMember.Read.All on this app registration first — turning this on without it makes Entra interrupt sign-in with a consent prompt. Names are read from each person's own token as they sign in, so a group nobody has signed in with keeps showing its ID. Display only: access checks always compare the ID."
            checked={identity.resolve_group_names === true}
            disabled={savingGroupNames}
            onCheckedChange={(next) => {
              setSavingGroupNames(true);
              // The parent rethrows after toasting, and the switch is driven by
              // the server-supplied value, so the failure is already handled.
              void Promise.resolve(onToggleGroupNameResolution(next))
                .catch(() => {})
                .finally(() => setSavingGroupNames(false));
            }}
          />
        ) : null}
      </div>

      {/* The wizard's verification rows, so a post-install edit is protected
          the same way the install was (docs/28 §Settings → Identity). */}
      {(testing || testResult?.checks) && (
        <div className="border-t border-border p-5">
          <IdpCheckList
            result={testResult?.checks ? { ok: testResult.ok, checks: testResult.checks } : null}
            running={testing}
            data-testid="idp-test-checks"
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 px-5 py-3 bg-muted/20 border-t border-border">
        <p className="flex-1 text-xs text-muted-foreground">
          Group claims drive RBAC across every app.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="default"
            onClick={() => void handleTest()}
            disabled={testing}
            className="h-11 md:h-9"
          >
            <RefreshCw
              className={cn('h-4 w-4', testing && 'motion-safe:animate-spin')}
              aria-hidden="true"
            />
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          <Button variant="outline" size="default" onClick={onReconfigure} className="h-11 md:h-9">
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
            Reconfigure
          </Button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3.5">
      <span className="shrink-0 text-sm text-foreground">{label}</span>
      {mono ? (
        <MonoValue copyable className="max-w-[280px] truncate" title={value}>
          {value}
        </MonoValue>
      ) : (
        <span className="text-sm text-muted-foreground">{value}</span>
      )}
    </div>
  );
}

// ── Reconfigure form ────────────────────────────────────────────────────────

function ReconfigureForm({
  identity,
  roleSource: currentRoleSource,
  onVerifyIdp,
  onReconfigure,
  onDone,
  allowCancel,
}: {
  identity: OrgSettings['identity'];
  roleSource: OrgSettings['role_source'];
  onVerifyIdp: (candidate: IdentityCandidate) => Promise<IdentityVerification>;
  onReconfigure: (body: ReconfigureIdentityInput) => Promise<{ ok: boolean; error?: string }>;
  onDone: () => void;
  allowCancel: boolean;
}): React.JSX.Element {
  const [validating, setValidating] = React.useState(false);
  const [verification, setVerification] = React.useState<IdentityVerification | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const initial: IdpFormInitial = {
    provider: identity?.provider ?? null,
    issuerUrl: identity?.oidc_issuer ?? '',
    clientId: identity?.client_id ?? '',
    displayName: identity?.display_name ?? '',
    // Default to the org's current mode; Google is always greenlight.
    roleSource: identity?.provider === 'google' ? 'greenlight' : (currentRoleSource ?? 'idp'),
    groupClaim: identity?.group_claim ?? 'groups',
    adminGroupId: identity?.role_groups?.org_admin?.[0] ?? '',
    itAdminGroupId: identity?.role_groups?.admin?.[0] ?? '',
    userGroupId: identity?.role_groups?.user?.[0] ?? '',
    hostedDomains: joinHostedDomains(identity?.hosted_domains),
    roleGroups: identity?.role_groups,
  };

  const form = useIdpForm({
    initial,
    // A secret belongs to one client at one issuer, so only an edit that keeps
    // that client can keep the stored Key Vault value; the server refuses the
    // rest.
    secretRequired: ({ sameProvider, sameIssuer, sameClientId }) =>
      !sameProvider || !sameIssuer || !sameClientId,
    rehydrateStoredOnSelect: true,
    onInvalidateValidation: () => setVerification(null),
  });

  // Verification covers the client ID and secret as well as the issuer, so it
  // needs them; a same-provider edit may keep the stored secret, which the
  // server reads from Key Vault rather than the form.
  const canValidate =
    form.provider !== null &&
    form.issuerUrl.trim() !== '' &&
    form.clientId.trim() !== '' &&
    (!form.secretRequired || form.clientSecret.trim() !== '');

  async function handleValidate(): Promise<void> {
    if (!canValidate || validating || !form.provider) return;
    setValidating(true);
    setVerification(null);
    try {
      setVerification(
        await onVerifyIdp({
          issuer_url: form.issuerUrl.trim(),
          provider: form.provider,
          client_id: form.clientId.trim(),
          ...(form.clientSecret.trim() ? { client_secret: form.clientSecret } : {}),
        }),
      );
    } finally {
      setValidating(false);
    }
  }

  // Save is gated on nothing having failed, not on every check passing.
  const canSave = verification?.ok === true && form.fieldsComplete;

  async function handleSave(): Promise<void> {
    if (!canSave || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await onReconfigure(form.buildBody());
      if (res.ok) onDone();
      else setSaveError(res.error ?? 'Could not save the configuration.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      headerBar
      title={identity ? 'Reconfigure identity provider' : 'Configure identity provider'}
    >
      <div className="space-y-6 p-5" data-testid="idp-reconfigure-form">
        <Alert variant="destructive" data-testid="idp-session-warning">
          <AlertTitle>Saving signs everyone out</AlertTitle>
          <AlertDescription>
            Reconfiguring the identity provider replaces the active IdP and invalidates all
            sessions, including yours. Everyone signs in again through the saved provider.
          </AlertDescription>
        </Alert>

        <IdpProviderPicker
          providerId={form.provider}
          legend="Provider"
          onSelect={(id) => form.selectProvider(id)}
        />

        {form.providerDef && (
          <>
            <IdpProviderFields
              form={form}
              redirectUri={`${window.location.origin}/auth/callback`}
              greenlightBlurb="Assign roles per user in the dashboard; group membership stops driving roles."
              googleRolesNote="Google Workspace roles are managed in the dashboard under Users — assign org admin, admin, and user there after sign-in."
              roleSourceExtra={
                /* Going-forward warnings on a mode change (docs/28 §Settings → Identity). */
                <RoleSourceChangeWarning current={currentRoleSource} next={form.roleSource} />
              }
            />

            {(validating || verification) && (
              <div className="space-y-3">
                {verification && (
                  <p
                    className={cn(
                      'text-sm',
                      verification.ok ? 'text-gl-green-text' : 'text-destructive',
                    )}
                    data-testid="idp-validate-msg"
                  >
                    {verification.message}
                  </p>
                )}
                <IdpCheckList
                  result={
                    verification?.checks
                      ? { ok: verification.ok, checks: verification.checks }
                      : null
                  }
                  running={validating}
                />
              </div>
            )}
            {saveError && (
              <p className="text-sm text-destructive" data-testid="idp-save-error">
                {saveError}
              </p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {allowCancel && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onDone}
                  className="h-11 md:h-9"
                  data-testid="idp-cancel"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleValidate()}
                disabled={validating || !canValidate}
                className="h-11 md:h-9"
                data-testid="idp-validate"
              >
                <RefreshCw
                  className={cn('h-4 w-4', validating && 'motion-safe:animate-spin')}
                  aria-hidden="true"
                />
                {validating ? 'Validating…' : 'Validate configuration'}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={!canSave || saving}
                className="h-11 md:h-9"
                data-testid="idp-save"
              >
                {saving ? 'Saving…' : 'Save & sign everyone out'}
              </Button>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

/** Warns when saving will change the org's role-management mode (docs/10). */
function RoleSourceChangeWarning({
  current,
  next,
}: {
  current: OrgSettings['role_source'];
  next: RoleSource;
}): React.JSX.Element | null {
  if (!current || next === current) return null;
  if (next === 'idp') {
    return (
      <Alert data-testid="idp-switch-to-idp-warning">
        <AlertDescription>
          At everyone&apos;s next sign-in, roles are recomputed from IdP groups — administrators not
          in the org admin group will be demoted. Configure the org admin group below before saving.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert data-testid="idp-switch-to-greenlight-warning">
      <AlertDescription>
        Current roles freeze and become manually managed. Group membership stops affecting roles
        immediately.
      </AlertDescription>
    </Alert>
  );
}
